/**
 * Discount code field (snippets/cart-discount.liquid), used by the cart drawer
 * and the cart page.
 *
 * Codes are saved with /cart/update.js. Nothing is rendered here: a
 * "cart:update" event tells the drawer (assets/cart-drawer.js) and the cart
 * page (assets/component-cart.js) to refresh their own totals, line prices
 * and code pills.
 */
window.BUTCartDiscount =
  window.BUTCartDiscount ||
  class BUTCartDiscount extends BUTCustomComponent {
    static SUCCESS_TIMEOUT = 4000;

    // Codes the cart holds after the last save, shared by every instance. The
    // pills only catch up once the cart has re-rendered, and never show a
    // shipping code, so they cannot be trusted for the next save.
    static codes = null;

    static isSame(a, b) {
      return a.toLowerCase() === b.toLowerCase();
    }

    // Titles of the discounts that actually reduce the cart or one of its lines.
    static getAppliedTitles(cart) {
      const lineLevel = (cart.items || []).flatMap((item) =>
        (item.line_level_discount_allocations || []).map(
          (allocation) => allocation.discount_application
        )
      );
      return [...(cart.cart_level_discount_applications || []), ...lineLevel].map(
        (application) => application.title
      );
    }

    constructor() {
      super();
      this.addEventListener("click", (event) => {
        if (event.target.closest(".js-discount-submit")) {
          this.apply();
          return;
        }
        const removeButton = event.target.closest(".js-remove-discount");
        if (removeButton) this.remove(removeButton);
      });
      this.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" || event.target !== this.input) return;
        // On the cart page the field sits inside the cart form.
        event.preventDefault();
        this.apply();
      });
      this.addEventListener("input", () => this.showMessage());
    }

    disconnectedCallback() {
      clearTimeout(this.messageTimer);
    }

    get input() {
      return this.$(".js-discount-input");
    }

    getVisibleCodes() {
      return Array.from(this.$$("[data-discount-code]"), (pill) => pill.dataset.discountCode);
    }

    async apply() {
      const code = this.input.value.trim();
      if (!code || this.hasAttribute("loading")) return;

      const visibleCodes = this.getVisibleCodes();
      if (visibleCodes.some((visible) => BUTCartDiscount.isSame(visible, code))) {
        this.input.value = "";
        this.showMessage("Duplicate");
        return;
      }

      const current = BUTCartDiscount.codes || visibleCodes;
      const cart = await this.save([
        ...current.filter((existing) => !BUTCartDiscount.isSame(existing, code)),
        code,
      ]);
      if (!cart) return;

      const saved = cart.discount_codes?.find((discount) =>
        BUTCartDiscount.isSame(discount.code, code)
      );
      if (!saved?.applicable) {
        this.showMessage("Invalid", "error");
        this.input.select();
        return;
      }

      this.input.value = "";
      const title = BUTCartDiscount.getAppliedTitles(cart).find((applied) =>
        BUTCartDiscount.isSame(applied, code)
      );
      // Accepted but not applied to anything in the cart: a shipping discount,
      // which only shows once an address is entered at checkout.
      if (title) this.showMessage("Applied", "success", title);
      else this.showMessage("Shipping");
    }

    async remove(removeButton) {
      if (this.hasAttribute("loading")) return;
      const code = removeButton.dataset.removeDiscount;
      // The pill is about to be re-rendered away, so keyboard focus moves on.
      if (removeButton.matches(":focus-visible")) this.input.focus({ preventScroll: true });

      const current = BUTCartDiscount.codes || this.getVisibleCodes();
      await this.save(current.filter((existing) => !BUTCartDiscount.isSame(existing, code)));
    }

    async save(codes) {
      this.toggleAttribute("loading", true);
      this.showMessage();

      try {
        const response = await fetch(`${window.routes.cart_update_url}.js`, {
          ...BUTHelper.fetchConfigJSON,
          body: JSON.stringify({ discount: codes.join(",") }),
        });
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

        const cart = await response.json();
        BUTCartDiscount.codes = (cart.discount_codes || [])
          .filter((discount) => discount.applicable)
          .map((discount) => discount.code);

        document.dispatchEvent(
          new CustomEvent("cart:update", {
            detail: { source: "discount", cartData: cart },
          })
        );
        return cart;
      } catch (error) {
        console.error(error);
        this.showMessage("Error", "error");
        return null;
      } finally {
        this.toggleAttribute("loading", false);
      }
    }

    // name: suffix of a data-message-* attribute; no name hides the message.
    showMessage(name, type = "info", code = "") {
      const message = this.$(".js-discount-message");
      if (!message) return;

      clearTimeout(this.messageTimer);
      const text = name ? this.dataset[`message${name}`] || "" : "";
      message.hidden = !text;
      message.dataset.type = type;
      message.querySelector(".js-discount-message-text").textContent = text.replace("[code]", code);

      if (type === "error" && text) this.input.setAttribute("aria-invalid", "true");
      else this.input.removeAttribute("aria-invalid");

      if (type === "success") {
        this.messageTimer = setTimeout(() => this.showMessage(), BUTCartDiscount.SUCCESS_TIMEOUT);
      }
    }
  };
if (!customElements.get("but-cart-discount")) {
  customElements.define("but-cart-discount", window.BUTCartDiscount);
}
